import type { Course } from '../types/content';
import { IMAGES } from './images';

const rangeBring = [
  'Your defensive handgun — rental available ($25)',
  'At least 2 magazines',
  '150 rounds of quality ammunition',
  'Eye and ear protection (provided if needed)',
  'Water and a snack',
  'Range fee ($10, paid at the range)',
];

const rangeProvide = [
  'Instruction from vetted HPP instructors',
  'Safe, structured live-fire drills',
  'Electronic hearing protection and eye protection if needed',
  'Targets and training aids',
];

const ccwBring = [
  'Valid government-issued photo ID',
  'Your handgun for qualification — rental available ($25)',
  '50 rounds of factory ammunition',
  'Eye and ear protection (provided if needed)',
  'Pen, water and a light lunch',
  'Range fee ($10, paid at the range)',
];

const ccwProvide = [
  'Sheriff-approved curriculum and student workbook',
  'Classroom instruction in the Bass Pro Shops Conservation Room',
  'Written test and live-fire qualification',
  'Certificate of completion for your sheriff application',
  'Electronic hearing protection and eye protection if needed',
];

const carbineBring = [
  'Your carbine (AR-platform recommended) with a sling',
  '3+ magazines',
  '300 rounds of quality ammunition',
  'Eye and ear protection (provided if needed)',
  'Water, sunscreen and a hat',
  'Range fee ($10, paid at the range)',
];

const nraBring = [
  'Valid photo ID',
  'Your handgun if the course includes live fire — rental available ($25)',
  'Ammunition as listed on your registration',
  'Eye and ear protection (provided if needed)',
  'Pen and water',
];

const nraProvide = [
  'Official NRA course materials',
  'Instruction from an NRA Training Counselor or certified instructor',
  'NRA course completion certificate',
  'Eye and ear protection if needed',
];

export const courses: Course[] = [
  {
    slug: 'multi-state-ccw',
    code: 'CCW',
    name: 'Multi-State CCW',
    benefit: 'Earn your Nevada permit plus training accepted toward multiple state permits.',
    overview:
      'Our sheriff-approved 8-hour initial course covers everything Nevada requires for a Concealed Firearms Permit — and the multi-state curriculum lets you apply for additional state permits. You will learn Nevada law, use of deadly force, safe carry and storage, then complete your live-fire qualification with an instructor beside you.',
    duration: '8 hours',
    schedule: '9:00am – 6:00pm in the classroom at the Bass Pro Shops Conservation Room, then range qualification.',
    price: 150,
    level: 'Beginner',
    categories: ['CCW'],
    image: IMAGES.classroom,
    learn: [
      'Nevada concealed carry law in plain English',
      'Use of deadly force and the force continuum',
      'Criminal and civil liability after a defensive incident',
      'Safe carry, storage and handling',
      'Holster selection and carry methods',
      'Written exam and live-fire qualification',
    ],
    bring: ccwBring,
    provide: ccwProvide,
    prerequisites: ['Age 21+ (18+ for active military)', 'No prior experience required — we coach you through qualification'],
    related: ['itdh', 'focc', 'fohd'],
  },
  {
    slug: 'nv-ccw-renewal',
    code: 'RENEW',
    name: 'NV CCW Renewal',
    benefit: 'The sheriff-approved 4-hour refresher to keep your Nevada permit current.',
    overview:
      'Renew your Nevada CCW with a focused 4-hour classroom refresher on law updates and use of force, followed by live-fire qualification. Efficient, current and taught by instructors who teach this every week.',
    duration: '4 hours',
    schedule: '9:00am – 1:00pm in the classroom, then range qualification.',
    price: 85,
    level: 'All levels',
    categories: ['CCW'],
    image: IMAGES.students,
    learn: [
      'Nevada law changes since your last permit',
      'Use of deadly force refresher',
      'Criminal and civil liability review',
      'Safe carry and storage best practices',
      'Live-fire qualification',
    ],
    bring: ccwBring,
    provide: ccwProvide,
    prerequisites: ['A current or recently expired Nevada CCW permit'],
    related: ['focc', 'ids', 'low-light'],
  },
  {
    slug: 'itdh',
    code: 'ITDH',
    name: 'Introduction to Defensive Handgun',
    benefit: 'Go from unsure to confident — safety, grip and defensive fundamentals. Powered by IDS.',
    overview:
      'The best first step for new shooters. Powered by Intuitive Defensive Shooting, ITDH builds safety as a skill and teaches how a defensive handgun actually works — then puts it into practice with coached, low-pressure live fire.',
    duration: '4 hours',
    schedule: 'Morning or afternoon sessions, classroom briefing then live fire on the range.',
    price: 150,
    level: 'Beginner',
    categories: ['Handgun'],
    image: IMAGES.grip,
    learn: [
      'Develop safety as a skill',
      'Choosing a defensive handgun',
      'Handgun manipulation',
      'Defensive shooting fundamentals',
      'Combat accuracy vs. target shooting',
      'Multiple target engagement',
      'End-of-course debrief',
    ],
    bring: rangeBring,
    provide: rangeProvide,
    prerequisites: ['None — designed for brand-new and returning shooters'],
    related: ['multi-state-ccw', 'fohd', 'focc'],
  },
  {
    slug: 'fohd',
    code: 'FOHD',
    name: 'Fundamentals of Home Defense',
    benefit: 'Build a real plan to protect your family and home.',
    overview:
      'Armed home defense is more than owning a firearm. FOHD covers layered home security, safe storage with children in the house, family plans, and the shooting skills that matter inside a home.',
    duration: '4 hours',
    schedule: 'Classroom and live-fire range session.',
    price: 175,
    level: 'Beginner',
    categories: ['Home Defense', 'Handgun'],
    image: IMAGES.homeDefense,
    learn: [
      'Layered home security and safe rooms',
      'Safe storage and quick access',
      'Creating a family defense plan',
      'Low-ready, movement and positions in the home',
      'Legal considerations of defending your home',
    ],
    bring: rangeBring,
    provide: rangeProvide,
    prerequisites: ['ITDH or equivalent safe handling experience'],
    related: ['itdh', 'focc', 'low-light'],
  },
  {
    slug: 'focc',
    code: 'FOCC',
    name: 'Fundamentals of Concealed Carry',
    benefit: 'Carry comfortably, draw safely and stay discreet every day.',
    overview:
      'Your permit says you may carry; FOCC teaches you how. Holster selection, safe drawing and reholstering, carry positions and the mindset of an everyday carrier — with coached repetitions on the range.',
    duration: '4 hours',
    schedule: 'Classroom and live-fire range session.',
    price: 175,
    level: 'Intermediate',
    categories: ['Handgun', 'CCW'],
    image: IMAGES.holster,
    learn: [
      'Holster and carry-position selection',
      'Drawing safely from concealment',
      'Reholstering without risk',
      'Situational awareness and avoidance',
      'Engaging from the draw under time',
    ],
    bring: [...rangeBring.slice(0, 1), 'A quality holster and a cover garment', ...rangeBring.slice(1)],
    provide: rangeProvide,
    prerequisites: ['ITDH or Multi-State CCW'],
    related: ['multi-state-ccw', 'ids', 'ddh'],
  },
  {
    slug: 'ids',
    code: 'IDS',
    name: 'Intuitive Defensive Shooting',
    benefit: 'Shoot the way your body reacts under stress — fast and accurate.',
    overview:
      'IDS trains the instinctive responses your body will use in a real encounter. Available as a one-day course or an immersive two-day program for students ready to sharpen speed and decision-making.',
    duration: '1 or 2 days',
    schedule: 'Full-day range sessions, 8:00am – 5:00pm.',
    price: 225,
    priceFrom: true,
    level: 'Intermediate',
    categories: ['Handgun'],
    image: IMAGES.students,
    learn: [
      'Stress response and how to train for it',
      'Threat-focused shooting',
      'Movement and shooting on the move',
      'Decision-making drills',
      'Close-quarters defensive techniques',
    ],
    bring: [...rangeBring.slice(0, 2), '300–500 rounds (1-day / 2-day)', ...rangeBring.slice(3)],
    provide: rangeProvide,
    prerequisites: ['FOCC or instructor approval'],
    related: ['focc', 'low-light', 'ddh'],
  },
  {
    slug: 'low-light',
    code: 'LLDH',
    name: 'Low Light Defensive Handgun',
    benefit: 'Most incidents happen in low light. Train for it.',
    overview:
      'Learn to identify threats, use handheld and weapon-mounted lights, and shoot accurately when visibility is limited — the conditions most defensive encounters actually happen in.',
    duration: '4 hours',
    schedule: 'Evening session, classroom then dim-light range work.',
    price: 175,
    level: 'Advanced',
    categories: ['Handgun'],
    image: IMAGES.lowlight,
    learn: [
      'Target identification in low light',
      'Handheld light techniques',
      'Weapon-mounted light use',
      'Movement and cover in darkness',
      'Reloads and malfunctions without light',
    ],
    bring: [...rangeBring.slice(0, 1), 'A handheld flashlight (weapon light optional)', ...rangeBring.slice(1)],
    provide: rangeProvide,
    prerequisites: ['IDS or instructor approval'],
    related: ['ids', 'ddh', 'fohd'],
  },
  {
    slug: 'ddh',
    code: 'DDH',
    name: 'Dynamic Defensive Handgun',
    benefit: 'Our capstone handgun course — real-world, dynamic, demanding.',
    overview:
      'The top of the handgun path. DDH combines movement, cover, decision-making and accuracy under pressure into dynamic drills that test everything you have built.',
    duration: '8 hours',
    schedule: 'Full-day range session, 8:00am – 5:00pm.',
    price: 250,
    level: 'Advanced',
    categories: ['Handgun'],
    image: IMAGES.hero,
    learn: [
      'Dynamic movement and use of cover',
      'Shooting from unconventional positions',
      'Scenario-based decision drills',
      'Malfunction clearing under stress',
      'Performance debrief and next-step plan',
    ],
    bring: [...rangeBring.slice(0, 2), '400 rounds of quality ammunition', ...rangeBring.slice(3)],
    provide: rangeProvide,
    prerequisites: ['IDS and Low Light, or instructor approval'],
    related: ['low-light', 'ids', 'ddc'],
  },
  {
    slug: 'itdc',
    code: 'ITDC',
    name: 'Introduction to Defensive Carbine',
    benefit: 'Safe, confident carbine handling for defense.',
    overview:
      'Learn the defensive carbine from the ground up: setup, sling use, zeroing, manipulations and fundamentals of accuracy at practical distances.',
    duration: '4 hours',
    schedule: 'Outdoor range session.',
    price: 175,
    level: 'Beginner',
    categories: ['Carbine', 'Home Defense'],
    image: IMAGES.carbine,
    learn: ['Carbine setup and sling use', 'Zeroing your optic or irons', 'Loading, unloading and malfunctions', 'Shooting positions', 'Defensive accuracy'],
    bring: carbineBring,
    provide: rangeProvide,
    prerequisites: ['Safe firearm handling experience'],
    related: ['ddc', 'fohd', 'itdh'],
  },
  {
    slug: 'ddc',
    code: 'DDC',
    name: 'Dynamic Defensive Carbine',
    benefit: 'Move, communicate and shoot the carbine under pressure.',
    overview:
      'Take carbine skills into dynamic territory with movement, transitions, barricades and multiple-target drills built around real defensive problems.',
    duration: '8 hours',
    schedule: 'Full-day outdoor range session.',
    price: 250,
    level: 'Advanced',
    categories: ['Carbine'],
    image: IMAGES.carbine,
    learn: ['Movement with a carbine', 'Barricade and cover use', 'Carbine-to-handgun transitions', 'Multiple target engagement', 'Shooting under time pressure'],
    bring: [...carbineBring.slice(0, 2), '500 rounds of quality ammunition', ...carbineBring.slice(3)],
    provide: rangeProvide,
    prerequisites: ['ITDC or instructor approval'],
    related: ['itdc', 'ddh', 'low-light'],
  },
  {
    slug: 'womens-ccw',
    code: 'W-CCW',
    name: "Women's Multi-State CCW",
    benefit: 'The full CCW course, women-only and taught by women.',
    overview:
      'The same sheriff-approved Multi-State CCW course, in a women-only classroom taught by women instructors. Ask anything, learn at your pace, and qualify with confidence.',
    duration: '8 hours',
    schedule: '9:00am – 6:00pm in the classroom, then range qualification.',
    price: 150,
    level: 'Beginner',
    categories: ['Women', 'CCW'],
    image: IMAGES.women,
    learn: ['Nevada concealed carry law', 'Use of deadly force', 'Carry options for women', 'Safe storage at home', 'Written exam and live-fire qualification'],
    bring: ccwBring,
    provide: ccwProvide,
    prerequisites: ['Age 21+', 'No prior experience required'],
    related: ['womens-itdh', 'multi-state-ccw', 'focc'],
  },
  {
    slug: 'womens-itdh',
    code: 'W-ITDH',
    name: "Women's Defensive Handgun",
    benefit: 'A supportive, women-only start to defensive shooting.',
    overview:
      'Our Introduction to Defensive Handgun curriculum in a women-only format — handgun fit for smaller hands, safe manipulation and coached live fire in a comfortable environment.',
    duration: '4 hours',
    schedule: 'Classroom briefing then live fire.',
    price: 150,
    level: 'Beginner',
    categories: ['Women', 'Handgun'],
    image: IMAGES.women,
    learn: ['Safety as a skill', 'Choosing a handgun that fits you', 'Grip, stance and recoil management', 'Defensive fundamentals', 'Carry and storage options'],
    bring: rangeBring,
    provide: rangeProvide,
    prerequisites: ['None'],
    related: ['womens-ccw', 'fohd', 'focc'],
  },
  {
    slug: 'nra-pistol-phase-2',
    code: 'NRA',
    name: 'NRA Basic Pistol — Phase II',
    benefit: 'The NRA standard for pistol knowledge, skills and attitude.',
    overview: 'Complete the NRA Basic Pistol course with hands-on instructor-led training and live fire.',
    duration: '4 hours',
    schedule: 'Classroom and range.',
    price: 125,
    level: 'Beginner',
    categories: ['NRA'],
    image: IMAGES.grip,
    learn: ['Pistol parts and operation', 'Fundamentals of shooting', 'Cleaning and storage', 'Live-fire qualification'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['Completion of NRA Basic Pistol Phase I (online)'],
    related: ['nra-ppith', 'itdh', 'nra-instructor-pistol'],
  },
  {
    slug: 'nra-ppith',
    code: 'PPITH',
    name: 'NRA Personal Protection Inside the Home',
    benefit: 'NRA-certified home-defense knowledge and skills.',
    overview: 'The NRA curriculum for safe, legal and effective armed defense of your home.',
    duration: '8 hours',
    schedule: 'Classroom and range.',
    price: 175,
    level: 'Intermediate',
    categories: ['NRA', 'Home Defense'],
    image: IMAGES.homeDefense,
    learn: ['Home security strategies', 'Defensive shooting techniques', 'Legal aspects of home defense', 'Live-fire exercises'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['NRA Basic Pistol or equivalent'],
    related: ['nra-ppoth', 'fohd', 'nra-instructor-ppith'],
  },
  {
    slug: 'nra-ppoth',
    code: 'PPOTH',
    name: 'NRA Personal Protection Outside the Home',
    benefit: 'The NRA course for defensive carry beyond your front door.',
    overview: 'Carry, drawing from concealment and defensive shooting skills to the NRA standard.',
    duration: '2 days',
    schedule: 'Two days, classroom and range.',
    price: 275,
    level: 'Intermediate',
    categories: ['NRA'],
    image: IMAGES.holster,
    learn: ['Concealed carry methods', 'Drawing from concealment', 'Shooting from cover', 'Legal and ethical considerations'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['NRA PPITH or instructor approval'],
    related: ['nra-ppith', 'focc', 'nra-instructor-ppoth'],
  },
  {
    slug: 'nra-rso',
    code: 'RSO',
    name: 'NRA Range Safety Officer',
    benefit: 'Learn to run a safe range and manage shooters.',
    overview: 'Become an NRA-certified Range Safety Officer, ready to supervise safe range operations.',
    duration: '9 hours',
    schedule: 'Classroom and range walkthrough.',
    price: 150,
    level: 'All levels',
    categories: ['NRA'],
    image: IMAGES.students,
    learn: ['RSO roles and responsibilities', 'Range standard operating procedures', 'Emergency response planning', 'Range inspections'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['Age 18+'],
    related: ['nra-crso', 'nra-pistol-phase-2', 'nra-instructor-pistol'],
  },
  {
    slug: 'nra-instructor-pistol',
    code: 'NRA-I',
    name: 'NRA Pistol Instructor',
    benefit: 'Become a certified NRA Pistol Instructor.',
    overview: 'Taught by an NRA Training Counselor. Learn to teach NRA Basic Pistol to new shooters.',
    duration: '2 days',
    schedule: 'Two days, classroom and range.',
    price: 350,
    level: 'Instructor',
    categories: ['NRA'],
    image: IMAGES.classroom,
    learn: ['NRA teaching methodology', 'Lesson planning and delivery', 'Coaching shooters', 'Pre-course qualification'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['Pre-course qualification', 'NRA Basic Pistol', 'Advanced shooting skills'],
    related: ['nra-instructor-ppith', 'nra-rso', 'nra-crso'],
  },
  {
    slug: 'nra-instructor-ppith',
    code: 'PPITH-I',
    name: 'NRA PPITH Instructor',
    benefit: 'Teach NRA Personal Protection Inside the Home.',
    overview: 'Certification to teach the NRA PPITH course.',
    duration: '1 day',
    schedule: 'Classroom and range.',
    price: 250,
    level: 'Instructor',
    categories: ['NRA'],
    image: IMAGES.classroom,
    learn: ['PPITH curriculum delivery', 'Teaching defensive concepts', 'Running live-fire exercises'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['NRA Pistol Instructor certification'],
    related: ['nra-instructor-ppoth', 'nra-instructor-pistol', 'nra-ppith'],
  },
  {
    slug: 'nra-instructor-ppoth',
    code: 'PPOTH-I',
    name: 'NRA PPOTH Instructor',
    benefit: 'Teach NRA Personal Protection Outside the Home.',
    overview: 'Certification to teach the NRA PPOTH course.',
    duration: '2 days',
    schedule: 'Two days, classroom and range.',
    price: 350,
    level: 'Instructor',
    categories: ['NRA'],
    image: IMAGES.classroom,
    learn: ['PPOTH curriculum delivery', 'Teaching concealed carry', 'Managing advanced drills'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['NRA PPITH Instructor certification'],
    related: ['nra-instructor-ppith', 'nra-instructor-pistol', 'nra-ppoth'],
  },
  {
    slug: 'nra-crso',
    code: 'CRSO',
    name: 'NRA Chief Range Safety Officer',
    benefit: 'Train and lead Range Safety Officers.',
    overview: 'The NRA certification for range leadership and RSO oversight.',
    duration: '1 day',
    schedule: 'Classroom.',
    price: 200,
    level: 'Instructor',
    categories: ['NRA'],
    image: IMAGES.students,
    learn: ['Range design and inspection', 'Teaching RSO courses', 'Range SOP development'],
    bring: nraBring,
    provide: nraProvide,
    prerequisites: ['Current NRA RSO certification'],
    related: ['nra-rso', 'nra-instructor-pistol', 'nra-instructor-ppith'],
  },
  {
    slug: 'uscca-dsf',
    code: 'USCCA',
    name: 'USCCA Defensive Shooting Fundamentals — Level 1',
    benefit: 'The USCCA foundation for responsible armed self-defense.',
    overview: 'USCCA Defensive Shooting Fundamentals Level 1 builds safe handling and core defensive shooting skills, taught by a USCCA-certified instructor.',
    duration: '4 hours',
    schedule: 'Classroom and range.',
    price: 150,
    level: 'Beginner',
    categories: ['USCCA', 'Handgun'],
    image: IMAGES.grip,
    learn: ['Firearm safety and handling', 'Fundamentals of marksmanship', 'Defensive mindset', 'Live-fire drills'],
    bring: rangeBring,
    provide: ['Official USCCA course materials', ...rangeProvide],
    prerequisites: ['None'],
    related: ['itdh', 'multi-state-ccw', 'focc'],
  },
  {
    slug: 'private',
    code: '1-ON-1',
    name: 'Private 1-on-1 & Group Training',
    benefit: 'Training built around you, your schedule and your goals.',
    overview: 'Private lessons for individuals, couples, families, companies and organizations.',
    duration: 'Flexible',
    schedule: 'Scheduled around you, 7 days a week.',
    price: 125,
    priceFrom: true,
    level: 'All levels',
    categories: ['Private', 'Women'],
    image: IMAGES.hero,
    learn: ['Custom curriculum', 'One-on-one coaching', 'Flexible scheduling'],
    bring: rangeBring,
    provide: rangeProvide,
    prerequisites: ['None'],
    related: ['itdh', 'multi-state-ccw', 'womens-itdh'],
    detailPath: '/private-training',
  },
];
